import React, { useCallback, useEffect, useState } from 'react';
import { resourceTypesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLocalization } from '../context/LocalizationContext';
import styles from '../styles/ResourceTypesPage.module.css';

const ResourceTypesPage = () => {
  const { can } = useAuth();
  const { t } = useLocalization();
  const [resourceTypes, setResourceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active',
  });

  const fetchResourceTypes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await resourceTypesAPI.getAll();
      setResourceTypes(response.data || []);
    } catch (error) {
      alert(error.response?.data?.message || t('resource_types_load_failed', 'Failed to load resource types'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchResourceTypes();
  }, [fetchResourceTypes]);

  const openModal = (item = null) => {
    if (item) {
      setEditingId(item.id);
      setFormData({
        name: item.name,
        description: item.description || '',
        status: item.status || 'active',
      });
    } else {
      setEditingId(null);
      setFormData({
        name: '',
        description: '',
        status: 'active',
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name.trim().toLowerCase(),
        description: formData.description,
        status: formData.status,
      };

      if (editingId) {
        await resourceTypesAPI.update(editingId, payload);
      } else {
        await resourceTypesAPI.create(payload);
      }

      closeModal();
      fetchResourceTypes();
    } catch (error) {
      alert(error.response?.data?.message || t('resource_types_save_failed', 'Failed to save resource type'));
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`${t('resource_types_delete_confirm_prefix', 'Delete resource type')} ${name}?`)) return;
    try {
      await resourceTypesAPI.delete(id);
      fetchResourceTypes();
    } catch (error) {
      alert(error.response?.data?.message || t('resource_types_delete_failed', 'Failed to delete resource type'));
    }
  };

  if (!can('resource_types', 'view')) {
    return <div className={styles.error}>{t('resource_types_permission_denied', 'You do not have permission to access this page.')}</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>
          <i className="bi bi-tags"></i> {t('resource_types_title', 'Resource Types')}
        </h1>
        <p className={styles.subtitle}>{t('resource_types_subtitle', 'Manage categories used by resources')}</p>
      </div>

      {loading ? (
        <div className={styles.loading}>{t('resource_types_loading', 'Loading resource types...')}</div>
      ) : (
        <div className={styles.content}>
          {can('resource_types', 'create') && (
            <button className={styles.createBtn} onClick={() => openModal()}>
              {t('resource_types_new', '+ New Resource Type')}
            </button>
          )}

          {resourceTypes.length === 0 ? (
            <div className={styles.empty}>{t('resource_types_empty', 'No resource types found.')}</div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('localization_name', 'Name')}</th>
                    <th>{t('tasks_label_description', 'Description')}</th>
                    <th>{t('users_status', 'Status')}</th>
                    <th>{t('users_actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {resourceTypes.map((item) => (
                    <tr key={item.id}>
                      <td className={styles.nameCell}>{item.name}</td>
                      <td>{item.description || '-'}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${styles[`status_${item.status}`]}`}>
                          {item.status === 'active' ? t('common_status_active', 'Active') : t('common_status_inactive', 'Inactive')}
                        </span>
                      </td>
                      <td className={styles.actionsCell}>
                        {can('resource_types', 'edit') && (
                          <button className={styles.editBtn} onClick={() => openModal(item)} title={t('teams_edit', 'Edit')}>
                            <i className="bi bi-pencil"></i>
                          </button>
                        )}
                        {can('resource_types', 'delete') && (
                          <button className={styles.deleteBtn} onClick={() => handleDelete(item.id, item.name)} title={t('teams_delete', 'Delete')}>
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h2>{editingId ? t('resource_types_modal_edit', 'Edit Resource Type') : t('resource_types_modal_new', 'New Resource Type')}</h2>
            <form onSubmit={handleSave}>
              <div className={styles.formGroup}>
                <label>{t('resource_types_name_required', 'Name *')}</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('resource_types_name_placeholder', 'e.g., machinery')}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('tasks_label_description', 'Description')}</label>
                <textarea
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('availability_custom_event_description_placeholder', 'Optional description')}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('users_status_required', 'Status *')}</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="active">{t('common_status_active', 'Active')}</option>
                  <option value="inactive">{t('common_status_inactive', 'Inactive')}</option>
                </select>
              </div>
              <div className={styles.modalButtons}>
                <button type="submit" className={styles.submitBtn}>
                  {editingId ? t('common_update', 'Update') : t('users_create', 'Create')}
                </button>
                <button type="button" className={styles.cancelBtn} onClick={closeModal}>{t('common_cancel', 'Cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceTypesPage;
